// Core
import { useState, useCallback, useRef, FocusEvent } from 'react';
// Packages
import cn from 'classnames';
// Components
import Navigation from 'components/Navigation';
import { useIsRailVisible } from './useIsRailVisible';
import { usePageScroll } from './usePageScroll';
// I18n
import { t } from 'i18n';
// Tokens
import { MOTION } from 'styles/tokens';
// Styles
import styles from './sideRail.module.scss';

interface IProps {
  isAuth?: boolean;
}

// The rail widens over $motion-base (200ms); a section's pages wait a little longer (antd takes
// seconds), so they never fly out beside a half-open rail.
const SUBMENU_OPEN_DELAY_S = MOTION.duration.slow / 1000;
// Collapsed, antd makes the menu `vertical` too, so a section's hover delay that runs out after the
// pointer has left would fly its pages out beside the narrow strip. Nothing opens until the rail has.
const NO_OPEN_SECTIONS: string[] = [];

// Icons-only strip on the left that opens into the full menu while the pointer (or keyboard focus)
// is on it, and stays open after a page is picked until the pointer leaves (Taras, 2026-10-15). It
// floats over the page, so the content never shifts when it opens. Open, the menu is `vertical`, so
// a section's pages fly out beside it on hover; an `inline` menu opens them only on a click, and the
// click already goes to the section's own page.
const SideRail = ({ isAuth }: IProps) => {
  const isVisible = useIsRailVisible(isAuth);
  const railRef = useRef<HTMLElement>(null);
  usePageScroll({ railRef, isActive: isVisible });

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleLeave = useCallback(() => setIsOpen(false), []);

  // Focus moving between items inside the rail is not leaving it.
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        handleLeave();
      }
    },
    [handleLeave],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <nav
      ref={railRef}
      aria-label={t('header.menu')}
      data-cy="sideRail"
      data-open={isOpen}
      className={cn(styles.rail, { [styles.railOpen]: isOpen })}
      onMouseEnter={handleOpen}
      onMouseLeave={handleLeave}
      onFocus={handleOpen}
      onBlur={handleBlur}
    >
      <Navigation
        isMenuVisible
        mode={isOpen ? 'vertical' : 'inline'}
        isCollapsed={isOpen ? undefined : true}
        openKeys={isOpen ? undefined : NO_OPEN_SECTIONS}
        subMenuOpenDelay={SUBMENU_OPEN_DELAY_S}
      />
    </nav>
  );
};

export default SideRail;
